import { _ } from "../../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Widget displaying a collection of items in an unordered list.
 *
 * @param {function} [itemRenderer] - Takes a item as argument and
 * should return a renderable. The default is to use the item itself
 * as renderable.
 * @param {object[]} [items] - The collection of objects you want to
 * render as list items. An object can be anything and will be passed
 * as argument to `itemRenderer`.
 * @param {string} [cssClass] - CSS classes to add to the root DOM
 * node.
 */
export default class ListWidget extends Widget {
	constructor({
		itemRenderer = (item) => item,
		items = [],
		cssClass = "list-stripped",
		itemDataTestName = "item",
		onItemClicked = (_item) => {},
		itemCssClass = (_item) => "",
		emptyContent = (html) => html.div(_("Nothing to show")),
	} = {}) {
		super(...arguments);

		this._itemRenderer = itemRenderer;
		this._items = items;
		this._cssClass = cssClass;
		this._itemDataTestName = itemDataTestName;
		this._onItemClicked = onItemClicked;
		this._itemCssClass = itemCssClass;
		this._emptyContent = emptyContent;
	}

	isEmpty() {
		return this._items.length === 0;
	}

	setItems(items = []) {
		this._items = items;
		this.update();
	}

	renderContentOn(html) {
		if (this.isEmpty()) {
			this._renderEmptyContentOn(html);
		} else {
			this._renderNonEmptyContentOn(html);
		}
	}

	_renderEmptyContentOn(html) {
		html.render(this._emptyContent);
	}

	_renderNonEmptyContentOn(html) {
		html.ul(
			{ klass: ["list", this._cssClass], "data-test": "items" },
			this._items.map((item) => this._listItemRenderer(item)),
		);
	}

	_listItemRenderer(item) {
		return (html) =>
			html.li(
				{
					"data-test": this._itemDataTestName,
					click: () => this._onItemClicked(item),
					class: this._itemCssClass(item),
				},
				this._itemRenderer(item),
			);
	}
}
